<script setup>
// Page 1 of the manager page — the promise, plus the instrument that proves it.
//
// Same shape as LandingHero: the headline splits into words and rises on load (it is
// the first thing visible, so it plays immediately rather than waiting on a scroll
// trigger), two civic accent squares drift as the page scrolls past, and the
// signature diagram sits in the right column.
//
// No eyebrow above the H1 and no check marks in the fact row — the headline carries
// its own weight, and a tick beside every item in a list of facts marks nothing. Both
// went from the homepage's hero for the same reason; this page follows it.
//
// Body copy is authored here in Dutch rather than in i18n, the way this page and the
// legal pages already do it — only the tab title and meta description live in nl.js.
// See VoorVveBeheerders.vue.
import { ref, onMounted, onUnmounted } from "vue";
import { ChevronDown, ArrowRight } from "@lucide/vue";
import PortfolioFlow from "@/components/beheerders/PortfolioFlow.vue";
import { buttonVariants } from "@/components/ui/button";
import { anchorScroll } from "@/lib/scroll";
import { gsap, useMotion, parallax, reducedMotion, EASE, splitHeadlineWords, vMagnetic } from "@/lib/motion";

// The first-screen proof row, in the order a manager weighs it: one login, money that
// stays separate, and what it costs. The middle one takes its own line at 390px and
// is made at length two screens down, so it is the one that sits out the phone
// layout — the same trade LandingHero makes.
const facts = [
  { key: "account", label: "Eén account voor al je VvE's" },
  { key: "separate", label: "Eigen rekening en eigen Mollie-account per VvE", phoneHidden: true },
  { key: "price", label: "€ 1 per actieve eenheid per maand, excl. btw" },
];

const root = ref(null);
const heroHeadline = ref(null);
const heroLead = ref(null);
const heroCtas = ref(null);
const heroFacts = ref(null);
const squareA = ref(null);
const squareB = ref(null);

let cleanup = () => {};

onMounted(() => {
  cleanup = useMotion(root.value, () => {
    if (!reducedMotion) {
      // Same reveal as the landing hero, masks and all — see splitHeadlineWords.
      const split = splitHeadlineWords(heroHeadline.value);
      gsap.set([heroLead.value, heroCtas.value, heroFacts.value], { opacity: 0, y: 16 });

      gsap
        .timeline({ delay: 0.1 })
        .to(split.words, { opacity: 1, y: "0%", duration: 0.7, ease: EASE, stagger: 0.045 })
        .to(heroLead.value, { opacity: 1, y: 0, duration: 0.5, ease: EASE }, "-=0.35")
        .to(heroCtas.value, { opacity: 1, y: 0, duration: 0.45, ease: EASE }, "-=0.25")
        .to(heroFacts.value, { opacity: 1, y: 0, duration: 0.45, ease: EASE }, "-=0.25");
    }

    parallax(squareA.value, { distance: 160 });
    parallax(squareB.value, { distance: -120 });
  });
});

onUnmounted(() => cleanup());
</script>

<template>
  <section ref="root" class="snap-page page-panel relative flex items-center overflow-hidden" data-page-label="Portefeuille">
    <div class="pointer-events-none absolute inset-0 -z-10" aria-hidden="true">
      <span ref="squareA" class="absolute right-[6%] top-24 hidden size-28 border-2 border-primary/25 lg:block" />
      <span ref="squareB" class="absolute left-[3%] bottom-16 hidden size-20 bg-secondary lg:block" />
    </div>

    <div class="mx-auto grid w-full max-w-6xl items-center gap-2.5 px-5 py-1 sm:gap-10 sm:py-12 lg:grid-cols-[1.05fr_1fr] lg:gap-14 lg:py-20">
      <div class="flex flex-col items-start">
        <h1
          ref="heroHeadline"
          class="max-w-xl font-sans text-[1.625rem] font-bold leading-[1.08] tracking-tight text-foreground sm:text-5xl sm:leading-[1.05] lg:text-6xl"
        >
          Meer VvE's, niet meer betaalwerk.
        </h1>
        <p ref="heroLead" class="mt-2 max-w-xl text-sm leading-snug text-muted-foreground sm:mt-6 sm:text-lg sm:leading-relaxed">
          Beheer je meer dan één VvE, dan is het innen van de bijdrage niet ingewikkeld — het is vooral herhaling. Pandig geeft elke VvE dezelfde
          incassostroom en jou één account om ertussen te wisselen. Het geld blijft per VvE op de eigen rekening.
        </p>
        <!-- The filled button is the LOW-commitment one, matching the homepage: a
             manager comparing tools wants to see how it is arranged before creating
             an account, and signing up sits right next to it for whoever is sold. -->
        <div ref="heroCtas" class="mt-3 flex w-full flex-col items-stretch gap-1.5 sm:mt-8 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
          <span v-magnetic class="flex sm:inline-block">
            <a href="#inrichting" :class="[buttonVariants({ size: 'lg' }), 'h-9 w-full sm:h-12 sm:w-auto']" @click="anchorScroll">
              Bekijk hoe het is ingericht<ChevronDown class="size-4" />
            </a>
          </span>
          <RouterLink
            :to="{ path: '/signup', query: { voor: 'beheerder' } }"
            :class="[buttonVariants({ variant: 'outline', size: 'lg' }), 'h-9 w-full sm:h-12 sm:w-auto']"
          >
            Start met een VvE<ArrowRight class="size-4" />
          </RouterLink>
        </div>
        <!-- Plain mono facts — quiet instrument copy, no list dressing. -->
        <ul ref="heroFacts" class="mt-2 flex flex-wrap gap-x-5 gap-y-0.5 sm:mt-6 sm:gap-y-2">
          <li
            v-for="f in facts"
            :key="f.key"
            class="font-mono text-[0.625rem] font-semibold uppercase tracking-[0.08em] text-muted-foreground sm:text-[0.6875rem]"
            :class="{ 'hidden sm:block': f.phoneHidden }"
          >
            {{ f.label }}
          </li>
        </ul>
      </div>

      <PortfolioFlow />
    </div>
  </section>
</template>
